import {
  CustomParameters,
  DeferredPromise,
  PaywallApi,
  PaywallFeature,
  PaywallResult,
  Plan,
} from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { RefObject } from 'react';
import { proxy, ref, useSnapshot } from 'valtio';

import devtoolsApi from '#/dev/devtools';
import { CloseHandler, ShowPaywallParams } from '#/plugins/paywall/models';
import { getDeferredPromise } from '#/service/utils';

import { authActions, authStore } from '../auth';
import { generalModalActions } from '../modals';
import { ClientPaywallApi, getPaywallApiFromRef } from './paywallApi';

export interface PaywallError {
  title: string;
  message: string;
}

export interface PaywallStore {
  apiRef?: RefObject<PaywallApi>;
  api: ClientPaywallApi;
  isShowing: boolean;
  error?: PaywallError;
  deferredPromise?: DeferredPromise<PaywallResult>;
  customParameters?: CustomParameters;
  closeHandler?: CloseHandler;
  productIds: string[];
  // paywall plugin is loaded, api endpoints are available
  isPluginReady: boolean;
  // paywall ready to use, plans are available
  isPaywallReady: boolean;
  plans: Plan[];
  supportedFeatures: PaywallFeature[];
  isFetchingPlans: boolean;
  hasFetchedPlans: boolean;
}

export const initialPaywallStore: PaywallStore = {
  isShowing: false,
  isPluginReady: false,
  isPaywallReady: false,
  productIds: [],
  isFetchingPlans: false,
  hasFetchedPlans: false,
  plans: [],
  supportedFeatures: [],
  api: {
    hasOwnUi: () => false,
    getProviderInfo: () => Promise.resolve({ name: 'unknown' }),
  },
};

export const paywallStore = proxy<PaywallStore>(initialPaywallStore);

export const paywallActions = {
  initPlugin: (apiRef: RefObject<PaywallApi>) => {
    paywallStore.apiRef = apiRef;

    return getPaywallApiFromRef(apiRef).then((api) => {
      paywallStore.api = ref(api);
      paywallStore.isPluginReady = true;

      return refreshSupportedFeatures();
    });
  },

  refreshPlans: async () => {
    paywallStore.isFetchingPlans = true;

    return paywallStore.api
      .getPlans?.()
      .then((plans) => {
        paywallStore.plans = plans.filter((p) =>
          paywallStore.productIds.includes(p.id)
        );
      })
      .catch((error) => {
        paywallStore.error = error;
      })
      .finally(() => {
        paywallStore.isFetchingPlans = false;
        paywallStore.hasFetchedPlans = true;
      });
  },

  setProductIds: (productIds: string[]) => {
    paywallStore.productIds = productIds;
    paywallStore.isPaywallReady = true;
  },

  showPaywall: (params?: ShowPaywallParams) => {
    if (!authStore.isLoggedIn) {
      authActions.showLogin();

      return Promise.resolve(PaywallResult.Aborted);
    }

    if (authStore.isEntitled) {
      generalModalActions.showModal({
        title: t('iap.alreadysubscribed.title'),
        message: t('iap.alreadysubscribed.message'),
        buttonLabel: t('button.ok'),
      });

      return Promise.resolve(PaywallResult.Aborted);
    }

    const _deferred = getDeferredPromise<PaywallResult>();
    paywallStore.isShowing = true;
    paywallStore.deferredPromise = _deferred;

    if (params?.closeHandler) {
      paywallStore.closeHandler = params.closeHandler;
    }

    if (params?.customParameters) {
      paywallStore.customParameters = params.customParameters;
    }

    return _deferred.promise;
  },

  closePaywall: () => {
    paywallStore.deferredPromise?.reject(PaywallResult.Aborted);
    paywallStore.deferredPromise = undefined;
    paywallStore.isShowing = false;
    paywallStore.error = undefined;
    paywallStore.customParameters = undefined;
    paywallStore.closeHandler = undefined;

    // we need to clear the purchase entitlements when we're exiting the paywall
    authActions.setPurchaseEntitlements();
  },

  setError: (error: PaywallError | undefined) => {
    paywallStore.error = error;
  },

  reset: () => {
    Object.keys(paywallStore).forEach((key) => {
      paywallStore[key] = initialPaywallStore[key];
    });
  },
};

export const usePaywall = () => useSnapshot(paywallStore) as PaywallStore;

const refreshSupportedFeatures = async () => {
  if (paywallStore.api.supportedFeatures) {
    return paywallStore.api
      .supportedFeatures()
      .then((features) => {
        paywallStore.supportedFeatures = features;
      })
      .catch((error) => {
        paywallStore.error = error;
        paywallStore.supportedFeatures = Object.values(PaywallFeature);
      });
  }

  // According to the existing logic, if the API doesn't support `supportedFeatures` method,
  // it means that all features are supported.
  paywallStore.supportedFeatures = Object.values(PaywallFeature);

  return Promise.resolve();
};

devtoolsApi.setStore('paywall', paywallStore, paywallActions);
